import { api, get, getPaginated } from '@/lib/api';
import type { PaginationMeta } from '@/lib/api';
import type { SupportMeta, SupportThread, SupportTicket } from './types';

export type SupportFilters = {
  status?: string;
  page?: number;
};

export function fetchTickets(
  filters: SupportFilters,
): Promise<{ data: SupportTicket[]; meta: PaginationMeta }> {
  return getPaginated<SupportTicket>('/support/tickets', filters);
}

export function fetchThread(reference: string): Promise<SupportThread> {
  return get<SupportThread>(`/support/tickets/${reference}`);
}

export function fetchSupportMeta(): Promise<SupportMeta> {
  return get<SupportMeta>('/support/meta');
}

export function fetchUnreadCount(): Promise<{ unread: number }> {
  return get<{ unread: number }>('/support/unread-count');
}

/**
 * Multipart, always — even with no files.
 *
 * The endpoint accepts attachments, and a request that switches between JSON
 * and FormData depending on whether the customer picked a screenshot is two
 * code paths where one will do. FormData works for both.
 */
export async function openTicket(payload: {
  subject: string;
  body: string;
  category: string;
  priority: string;
  screen?: string;
  attachments: File[];
}): Promise<SupportTicket> {
  const form = new FormData();

  form.append('subject', payload.subject);
  form.append('body', payload.body);
  form.append('category', payload.category);
  form.append('priority', payload.priority);

  // Which screen they were on. Diagnostic context the customer should never
  // have to type, and the difference between "it's broken" and a reproduction.
  if (payload.screen !== undefined) form.append('screen', payload.screen);

  payload.attachments.forEach((file) => form.append('attachments[]', file));

  const { data } = await api.post<{ data: SupportTicket }>('/support/tickets', form);

  return data.data;
}

export async function replyToTicket(
  reference: string,
  body: string,
  attachments: File[] = [],
): Promise<SupportThread> {
  const form = new FormData();

  form.append('body', body);
  attachments.forEach((file) => form.append('attachments[]', file));

  const { data } = await api.post<{ data: SupportThread }>(
    `/support/tickets/${reference}/messages`,
    form,
  );

  return data.data;
}

export async function setTicketStatus(
  reference: string,
  status: 'closed' | 'open',
): Promise<SupportTicket> {
  const { data } = await api.put<{ data: SupportTicket }>(
    `/support/tickets/${reference}/status`,
    { status },
  );

  return data.data;
}
